// The Doc Pabean table, shared by /doc-pabean and /monitoring-blokir (revamp PR 7a). Same props, rows, client
// filters, access rules, row actions and requests as the Mantine table it replaces; the table is the Lumen DataTable,
// the chips are Lumen Badges in the #15 tones, and phones get the same card list as before.
import {
  CircleCheck,
  Copy,
  Eye,
  FileText,
  Info,
  Loader2,
  Pencil,
  Plane,
  RefreshCw,
  RotateCcw,
  Ship,
  Trash2,
  Upload,
} from 'lucide-react';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  DataTable,
  type DataTableColumn,
  DataTableMono,
  DataTableRowAction,
  DataTableRowActions,
} from '@/components/data-table';
import { Badge } from '@/components/lumen/badge';
import { Button } from '@/components/lumen/button';
import { Input } from '@/components/lumen/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/lumen/popover';
import { FilterSelect } from './filter-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { cn } from '@/lib/utils';
import { formatTemplate } from '@/lib/format-template';
import { mapStatusString, resolveStatusTone } from '@components/commons/status-chip';
import { useLanguage } from '@contexts/LanguageContext';
import { flagAssetUrl, toFlagCountryCode } from '@utils/country-flag';
import { type MotType, resolveMot } from '@utils/mot';
import { useIsMobile } from './use-is-mobile';
import type { DocPabean, DocPabeanStatus } from '../../app/(dashboard)/doc-pabean/apis/models/doc-pabean-model';
import useCopyDocPabean from '../../app/(dashboard)/doc-pabean/apis/use-cases/use-copy-doc-pabean';
import useDeleteDocPabean from '../../app/(dashboard)/doc-pabean/apis/use-cases/use-delete-doc-pabean';
import useRestoreDocPabean from '../../app/(dashboard)/doc-pabean/apis/use-cases/use-restore-doc-pabean';
import useSyncRespon from '../../app/(dashboard)/doc-pabean/apis/use-cases/use-sync-respon';
import { CompanyNameCell } from './CompanyNameCell';
import DeclarationPreviewModal from './DeclarationPreviewModal';
import StatusHistoryModal from './StatusHistoryModal';
import { StatusHistoryIconButton } from './StatusHistoryModal';
import UnggahBerkasModal from './UnggahBerkasModal';

// Email-based access rules for BC 2.0 (doc-pabean / import)
// 'edit' = full edit access, 'view' = view-only (eye icon), undefined = no row access restriction (default edit)
const BC20_ACCESS_RULES: Record<string, 'edit' | 'view'> = {
  'anne.veronica@molgroup.com': 'view',
  'ikumi.shibata1@molgroup.com': 'view',
  'panji@gateway-id.com': 'view',
  'christina.vonny@molgroup.com': 'edit',
  'ari.wibowo@molgroup.com': 'edit',
  'femy.muthia@molgroup.com': 'edit',
  'lanny.apriliany@molgroup.com': 'edit',
  'mlg-id.seaimport01@molgroup.com': 'edit',
  'mlg-id.airport01@molgroup.com': 'edit',
};

// Email-based access rules for BC 3.0 (export)
const BC30_ACCESS_RULES: Record<string, 'edit' | 'view'> = {
  'panji@gateway-id.com': 'view',
  'christina.vonny@molgroup.com': 'edit',
  'ari.wibowo@molgroup.com': 'edit',
  'femy.muthia@molgroup.com': 'edit',
  'reza.haqqa@molgroup.com': 'edit',
  'ratri.puji@molgroup.com': 'edit',
  'prama.adi@molgroup.com': 'edit',
  'mlg-id.airexport01@molgroup.com': 'edit',
};

/**
 * Copy POSTs can take several seconds. Calling window.open() after await loses
 * the user-gesture token and browsers silently block the tab. Open a blank tab
 * synchronously on click, then navigate it once the UUID is known. If the
 * popup is blocked, fall back to same-tab navigation.
 */
function navigateCopiedDocument(targetUrl: string, newTab: Window | null) {
  const absoluteUrl = new URL(targetUrl, window.location.origin).href;
  if (newTab && !newTab.closed) {
    newTab.location.href = absoluteUrl;
    return;
  }
  window.location.assign(absoluteUrl);
}

/** Build draft-edit query string: optional isViewOnly + respon (namaProses). */
function buildDraftEditQuery(options: { isViewOnly?: boolean; respon?: string | null }) {
  const params = new URLSearchParams();
  if (options.isViewOnly) {
    params.set('isViewOnly', 'true');
  }
  const respon = options.respon?.trim();
  if (respon) {
    params.set('respon', respon);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

const getUserEmail = (): string => {
  try {
    const raw = localStorage.getItem('authData');
    if (!raw) return '';
    return (JSON.parse(raw).email || '').toLowerCase();
  } catch {
    return '';
  }
};

const isExportAju = (nomorAju: string) => nomorAju.startsWith('000030');

/** The row's access level for its direction: listed users get theirs, other molgroup users view, everyone else edits. */
const isViewOnlyFor = (item: DocPabean) => {
  const userEmail = getUserEmail();
  const isMolgroup = userEmail.endsWith('@molgroup.com');
  const rules = isExportAju(item.nomorAju) ? BC30_ACCESS_RULES : BC20_ACCESS_RULES;
  return (rules[userEmail] || (isMolgroup ? 'view' : 'edit')) === 'view';
};

/** `040300012345…` → `040300-012345-20261027-…`, whatever dashes the value already had. */
const formatNomorAju = (value: string) => {
  if (!value) return '-';
  const raw = value.replace(/-/g, '');
  if (raw.length <= 6) return raw;
  if (raw.length <= 12) return `${raw.slice(0, 6)}-${raw.slice(6)}`;
  if (raw.length <= 20) return `${raw.slice(0, 6)}-${raw.slice(6, 12)}-${raw.slice(12)}`;
  return `${raw.slice(0, 6)}-${raw.slice(6, 12)}-${raw.slice(12, 20)}-${raw.slice(20)}`;
};

/** Verified bundle: the row carries an Invoice (380) and a BL/MBL or AWB/MAWB. */
const isVerifiedBundle = (item: DocPabean) => {
  const docTypes = new Set(
    (item.files || [])
      .map((f) =>
        String(f.documentType || '')
          .toUpperCase()
          .trim()
      )
      .filter(Boolean)
  );
  const hasInvoice = docTypes.has('380') || docTypes.has('I');
  const hasBl = docTypes.has('705') || docTypes.has('704') || docTypes.has('BL') || docTypes.has('MBL');
  const hasAwb = docTypes.has('740') || docTypes.has('741') || docTypes.has('AWB') || docTypes.has('MAWB');
  return hasInvoice && (hasBl || hasAwb);
};

const formatStatusDateTime = (dateStr: string): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const formatRelativeTime = (dateStr: string | null | undefined, tr?: (key: string) => string): string | null => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return null;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const ago = tr ? tr('docpabean.ago') : 'ago';
  if (mins < 1) return tr ? tr('docpabean.justNow') : 'just now';
  if (mins < 60) return `${mins}m ${ago}`;
  if (hours < 24) {
    const remMins = mins % 60;
    return remMins > 0 ? `${hours}h ${remMins}m ${ago}` : `${hours}h ${ago}`;
  }
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h ${ago}` : `${days}d ${ago}`;
};

/** `HH:mm` of createdAt, in the browser's zone. */
const getTimeFromCreatedAt = (createdAt: string | null | undefined): string | null => {
  if (!createdAt) return null;
  try {
    const createdDate = new Date(createdAt);
    const hours = createdDate.getHours().toString().padStart(2, '0');
    const minutes = createdDate.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return null;
  }
};

const APPROVAL_LABEL_KEYS: Record<string, string> = {
  APPROVED: 'docpabean.approved',
  WAITING_FOR_REVIEW: 'docpabean.waiting',
  NEED_REVISION: 'docpabean.needRevision',
  REJECTED: 'docpabean.rejected',
  CANCELLED: 'docpabean.cancelled',
  SCHEDULED: 'docpabean.scheduled',
};

/** A CEISA respon or status label → its badge tone (#15): green states ok, red states err, the rest neutral. */
const STATUS_TONE = { green: 'ok', red: 'err', purple: 'ai', neutral: 'neutral' } as const;
const statusTone = (status: string) => STATUS_TONE[resolveStatusTone(status)];

/** Import and export as two categorical tones (the old purple and green), on the Doc Pabean and Draft tables alike. */
export function ShipmentTypeBadge({ isExport, short = false }: { isExport: boolean; short?: boolean }) {
  const { t } = useLanguage();
  const name = isExport ? t('docpabean.export') : t('docpabean.import');
  return (
    <Badge variant={isExport ? 'ok' : 'ai'} title={name} className="uppercase">
      {short ? (isExport ? 'E' : 'I') : name}
    </Badge>
  );
}

/** The mode of transport as a word with its icon, not a bare icon (a 16px ship and plane look alike in a row). */
function MotBadge({ mot }: { mot: MotType }) {
  return (
    <Badge variant="neutral" className="gap-1">
      {mot === 'SEA' ? <Ship aria-hidden /> : <Plane aria-hidden />}
      {mot}
    </Badge>
  );
}

function StatusBadge({ status }: { status: string }) {
  const label = mapStatusString(status).toUpperCase();
  return (
    <Badge variant={statusTone(status)} title={label} className="whitespace-nowrap">
      {label}
    </Badge>
  );
}

/** The latest CEISA status with an info button that lists every status, newest first. */
function StatusCellWithInfo({ statuses }: { statuses: DocPabeanStatus[] }) {
  const { t } = useLanguage();
  const firstStatus = statuses[0];
  const label = (firstStatus.keterangan || '-').toUpperCase();
  return (
    <div className="flex items-center justify-center gap-1">
      <Badge variant={statusTone(firstStatus.keterangan || '-')} className="whitespace-nowrap">
        {label}
      </Badge>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={t('docpabean.statusHistory')}
                data-action="status-info"
                className="shrink-0 text-label hover:text-label"
                onClick={(e) => e.stopPropagation()}
              >
                <Info />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('docpabean.statusHistory')}</TooltipContent>
        </Tooltip>
        <PopoverContent className="max-h-[50vh] w-80 overflow-y-auto p-0">
          <p className="sticky top-0 z-[1] bg-raised px-3 pt-3 pb-2 font-semibold text-fg text-sm">
            {t('docpabean.statusHistory')}
          </p>
          <ol className="grid gap-2 px-3 pb-3">
            {statuses.map((s, idx) => (
              <li
                key={s.id}
                className={cn('rounded-control border border-line px-2 py-1.5', idx === 0 ? 'bg-tint' : 'bg-surface-2')}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-fg text-xs">{s.keterangan}</span>
                  {idx === 0 && <Badge variant="outline">{t('docpabean.latest')}</Badge>}
                </div>
                {s.kodeRespon && (
                  <p className="text-fg-muted text-xs">
                    {t('docpabean.respon')}: <DataTableMono>{s.kodeRespon}</DataTableMono>
                    {s.nomorRespon ? ' - ' : ''}
                    {s.nomorRespon && <DataTableMono>{s.nomorRespon}</DataTableMono>}
                  </p>
                )}
                <p className="text-fg-muted text-xs">{formatStatusDateTime(s.createdAt)}</p>
              </li>
            ))}
          </ol>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** A row action with an inline confirm: Delete (soft, the CEISA filing is untouched) or Restore for deleted rows. */
function ConfirmRowAction({
  item,
  mode,
  size,
}: {
  item: DocPabean;
  mode: 'delete' | 'restore';
  size?: 'icon-sm';
}) {
  const { t } = useLanguage();
  const [opened, setOpened] = useState(false);
  const { deleteDocPabean, loading: deleting } = useDeleteDocPabean();
  const { restoreDocPabean, loading: restoring } = useRestoreDocPabean();
  const isDelete = mode === 'delete';
  const loading = isDelete ? deleting : restoring;
  const label = isDelete ? t('docpabean.tooltipDelete') : t('docpabean.tooltipRestore');
  const icon = loading ? (
    <Loader2 className="animate-spin motion-reduce:animate-none" />
  ) : isDelete ? (
    <Trash2 />
  ) : (
    <RotateCcw />
  );

  return (
    <Popover open={opened} onOpenChange={setOpened}>
      <PopoverTrigger asChild>
        {size ? (
          <Button
            variant="ghost"
            size={size}
            aria-label={label}
            data-action={mode}
            className={
              isDelete ? 'text-err hover:bg-err/10 hover:text-err-ink' : 'text-ok hover:bg-ok/10 hover:text-ok-ink'
            }
          >
            {icon}
          </Button>
        ) : (
          <DataTableRowAction label={label} tone={isDelete ? 'danger' : 'ok'} data-action={mode}>
            {icon}
          </DataTableRowAction>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-3">
        <div className="grid gap-2">
          <p className="text-fg text-sm">
            {formatTemplate(isDelete ? t('docpabean.deleteConfirm') : t('docpabean.restoreConfirm'), {
              nomor: item.nomorAju,
            })}
          </p>
          {/* Already filed at CEISA: deleting here only hides the row, so say so plainly. */}
          {isDelete && item.idHeader ? (
            <p className="font-medium text-warn-ink text-xs">{t('docpabean.deleteFiledWarning')}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button size="xs" variant="outline" onClick={() => setOpened(false)}>
              {t('docpabean.deleteCancel')}
            </Button>
            <Button
              size="xs"
              variant={isDelete ? 'destructive' : 'default'}
              className={isDelete ? undefined : 'bg-ok bg-none text-white shadow-none hover:brightness-110'}
              disabled={loading}
              data-confirm={mode}
              onClick={async () => {
                const ok = isDelete ? await deleteDocPabean(item.id) : await restoreDocPabean(item.id);
                if (ok) setOpened(false);
              }}
            >
              {isDelete ? t('docpabean.delete') : t('docpabean.restore')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const SPIN = <Loader2 className="animate-spin motion-reduce:animate-none" />;

type RowHandlers = {
  onView: (item: DocPabean) => void;
  onCopy: (item: DocPabean) => void;
  onUpload: (item: DocPabean) => void;
  onPreviewPdf: (item: DocPabean) => void;
  onSyncRespon: (item: DocPabean) => void;
  copyLoading: boolean;
  syncResponLoadingAju: string | null;
};

/**
 * Phone-sized viewports can't fit the table: one card per row with the same actions as the old card list (View,
 * PDF, Copy, Upload, Sync, Delete/Restore) and its prev/next pager.
 */
function DocPabeanMobileCards({
  records,
  loading,
  page,
  pageSize,
  totalRecords,
  onPageChange,
  onView,
  onCopy,
  onUpload,
  onPreviewPdf,
  onSyncRespon,
  copyLoading,
  syncResponLoadingAju,
}: RowHandlers & {
  records: DocPabean[];
  loading?: boolean;
  page: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useLanguage();
  const lastPage = Math.max(1, Math.ceil(totalRecords / Math.max(1, pageSize)));

  if (loading && records.length === 0) {
    return (
      <p className="flex items-center justify-center gap-2 py-8 text-fg-muted text-sm">
        {SPIN}
        {t('docpabean.loadingData')}
      </p>
    );
  }
  if (records.length === 0) {
    return <p className="py-8 text-center text-fg-muted text-sm">{t('docpabean.emptyFilter')}</p>;
  }

  return (
    <div className="grid gap-3">
      {records.map((item) => {
        const isExport = isExportAju(item.nomorAju);
        const mot = resolveMot(item.motType);
        const iso2 = toFlagCountryCode(item.shipperConsigneeCountry);
        const flagSrc = flagAssetUrl(iso2);
        const field = (label: string, value: React.ReactNode) => (
          <p className="text-fg-muted text-xs">
            <span className="font-semibold text-fg">{label}:</span> {value}
          </p>
        );
        return (
          <div key={item.batchUUID} className="rounded-card border border-line bg-raised p-3">
            <div className="mb-1.5 flex items-start justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5">
                <DataTableMono className="break-words font-bold text-fg text-sm">
                  {formatNomorAju(item.nomorAju)}
                </DataTableMono>
                {isVerifiedBundle(item) && (
                  <CircleCheck
                    aria-label={t('docpabean.tooltipVerified')}
                    className="size-3.5 shrink-0 text-fg-muted"
                  />
                )}
              </span>
              <ShipmentTypeBadge isExport={isExport} />
            </div>
            <div className="mb-2 grid gap-0.5">
              {field(t('docpabean.company'), item.namaPerusahaan || '-')}
              {item.namaProses && field(t('docpabean.proses'), item.namaProses)}
              {item.drafterName && field(t('docpabean.colDrafter'), item.drafterName)}
              {field(t('docpabean.totalItemLabel'), item.totalItem ?? 0)}
            </div>
            {(iso2 || mot) && (
              <div className="mb-2 flex items-center gap-1.5">
                {iso2 && (
                  <span className="flex items-center gap-1">
                    {flagSrc && (
                      <img
                        src={flagSrc}
                        alt="" // decorative: the country code is shown next to the flag
                        loading="lazy"
                        className="block h-3 w-4 rounded-[2px] object-cover shadow-[inset_0_0_0_1px_var(--lm-line)]"
                      />
                    )}
                    <span className="font-mono font-semibold text-[10px] text-fg-muted">{iso2}</span>
                  </span>
                )}
                {mot && <MotBadge mot={mot} />}
              </div>
            )}
            {/* The old card list showed Copy and Delete whatever the user's access level; kept as it was. */}
            <div className="flex justify-end gap-1">
              <MobileAction label={t('docpabean.tooltipViewEdit')} action="view" onClick={() => onView(item)}>
                <Eye />
              </MobileAction>
              <MobileAction
                label={t(item.nomorAju ? 'docpabean.tooltipPreviewPdf' : 'docpabean.tooltipPreviewPdfNoAju')}
                action="pdf"
                disabled={!item.nomorAju}
                onClick={() => onPreviewPdf(item)}
              >
                <FileText />
              </MobileAction>
              <MobileAction
                label={t('docpabean.tooltipCopy')}
                action="copy"
                disabled={copyLoading}
                onClick={() => onCopy(item)}
              >
                {copyLoading ? SPIN : <Copy />}
              </MobileAction>
              <MobileAction label={t('docpabean.tooltipUpload')} action="upload" onClick={() => onUpload(item)}>
                <Upload />
              </MobileAction>
              <MobileAction
                label={t('docpabean.tooltipSyncRespon')}
                action="sync"
                disabled={syncResponLoadingAju === item.nomorAju}
                onClick={() => onSyncRespon(item)}
              >
                {syncResponLoadingAju === item.nomorAju ? SPIN : <RefreshCw />}
              </MobileAction>
              <ConfirmRowAction item={item} mode={item.isDeleted ? 'restore' : 'delete'} size="icon-sm" />
            </div>
          </div>
        );
      })}
      <div className="flex items-center justify-between px-1">
        <span className="text-fg-muted text-xs">
          {t('docpabean.page')} {page} / {lastPage} · {totalRecords} total
        </span>
        <div className="flex gap-1.5">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="‹"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          >
            ‹
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="›"
            disabled={page >= lastPage}
            onClick={() => onPageChange(Math.min(lastPage, page + 1))}
          >
            ›
          </Button>
        </div>
      </div>
    </div>
  );
}

function MobileAction({
  label,
  action,
  children,
  ...props
}: React.ComponentProps<typeof Button> & { label: string; action: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={label} data-action={action} {...props}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

interface DocPabeanTableProps {
  data: DocPabean[];
  numberPageSize: number;
  numberPage: number;
  loading?: boolean;
  setNumberPageSize: (value: number) => void;
  onSearch?: (query: string) => void;
  totalPage?: number;
  onPageChange?: (page: number) => void;
}

export default function DocPabeanTable({
  data,
  numberPageSize,
  numberPage,
  loading = false,
  setNumberPageSize,
  totalPage = 0,
  onPageChange,
}: DocPabeanTableProps) {
  const { t } = useLanguage();
  const [pageSize, setPageSize] = useState(numberPageSize);
  const [records, setRecords] = useState<DocPabean[]>(data.slice(0, pageSize));

  // Unggah Berkas is keyed by batchUUID (the modal fetches the batch's CEISA edit documents); the PDF preview by AJU.
  const [unggahBerkasOpen, setUnggahBerkasOpen] = useState(false);
  const [unggahBerkasBatchUUID, setUnggahBerkasBatchUUID] = useState('');
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewAju, setPdfPreviewAju] = useState<string | null>(null);
  // Status history state lifted to table level (DataTable remounts cells on every render).
  const [history, setHistory] = useState<{ ajuNo: string; totalItem?: number; shipperCountry?: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const openPdfPreview = (item: DocPabean) => {
    if (!item.nomorAju) return;
    setPdfPreviewAju(item.nomorAju);
    setPdfPreviewOpen(true);
  };
  const openUpload = (item: DocPabean) => {
    setUnggahBerkasBatchUUID(item.batchUUID);
    setUnggahBerkasOpen(true);
  };
  const [filters, setFilters] = useState({
    shipmentType: '',
    namaPerusahaan: '',
    drafterName: '',
    motType: '',
    shipperConsigneeCountry: '',
  });
  const { copyDocPabean, loading: copyLoading } = useCopyDocPabean();
  const { syncRespon, loadingAju: syncResponLoadingAju } = useSyncRespon();
  const isFirstRender = useRef(true);
  const isMobile = useIsMobile();

  const filteredRecords = records.filter((item) => {
    const matchesShipmentType =
      !filters.shipmentType || (isExportAju(item.nomorAju) ? 'export' : 'import') === filters.shipmentType;
    const matchesNamaPerusahaan =
      !filters.namaPerusahaan ||
      (item.senderName || item.namaPerusahaan)?.toLowerCase().includes(filters.namaPerusahaan.toLowerCase());
    const matchesDrafterName =
      !filters.drafterName || item.drafterName?.toLowerCase().includes(filters.drafterName.toLowerCase());
    // resolveMot, not a substring test: "LAUT" is a sea filing and an empty motType is not an air one.
    const matchesMotType = !filters.motType || resolveMot(item.motType) === filters.motType;
    const matchesCountry =
      !filters.shipperConsigneeCountry ||
      item.shipperConsigneeCountry?.toLowerCase().includes(filters.shipperConsigneeCountry.toLowerCase());
    return matchesShipmentType && matchesNamaPerusahaan && matchesDrafterName && matchesMotType && matchesCountry;
  });

  const setFilter = (key: keyof typeof filters) => (value: string) => setFilters((prev) => ({ ...prev, [key]: value }));

  const handleCopy = async (item: DocPabean) => {
    const isImport = !isExportAju(item.nomorAju);
    // Must open before await — see navigateCopiedDocument.
    const newTab = window.open('about:blank', '_blank');
    try {
      const batchUUID = await copyDocPabean(item.batchUUID, isImport ? 'import' : 'export');
      if (!batchUUID) {
        newTab?.close();
        return;
      }
      const targetUrl = isImport
        ? `/doc-pabean/draft/${batchUUID}/edit?fromCopy=true`
        : `/export?batchUUID=${batchUUID}&fromCopy=true`;
      navigateCopiedDocument(targetUrl, newTab);
    } catch (error) {
      newTab?.close();
      console.error('Copy doc pabean failed:', error);
    }
  };

  const handleEdit = (item: DocPabean) => {
    const isViewOnly = isViewOnlyFor(item);
    if (!isExportAju(item.nomorAju)) {
      const query = buildDraftEditQuery({ isViewOnly, respon: item.namaProses });
      window.open(`/doc-pabean/draft/${item.batchUUID}/edit${query}`, '_blank');
    } else {
      const viewOnlyParam = isViewOnly ? '&isViewOnly=true' : '';
      window.open(`/export?batchUUID=${item.batchUUID}${viewOnlyParam}`, '_blank');
    }
  };

  // The old card list built the export URL with a second '?' (`/export?batchUUID=…?isViewOnly=true`); kept as it was.
  const handleMobileView = (item: DocPabean) => {
    const access = isViewOnlyFor(item) ? 'view' : 'edit';
    if (isExportAju(item.nomorAju)) {
      const viewOnlyParam = access === 'view' ? '?isViewOnly=true' : '';
      window.open(`/export?batchUUID=${item.batchUUID}${viewOnlyParam}`, '_blank');
    } else {
      const query = buildDraftEditQuery({ isViewOnly: access === 'view', respon: item.namaProses });
      window.open(`/doc-pabean/draft/${item.batchUUID}/edit${query}`, '_blank');
    }
  };

  const dash = <span className="text-fg-muted">-</span>;

  const columns: DataTableColumn<DocPabean>[] = [
    {
      accessor: 'nomorAju',
      title: t('docpabean.colNomorAju'),
      textAlign: 'center',
      render: (item) => (
        <span className="inline-flex items-center justify-center gap-1.5">
          <DataTableMono className="text-fg text-xs">{formatNomorAju(item.nomorAju)}</DataTableMono>
          {isVerifiedBundle(item) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span aria-label={t('docpabean.tooltipVerified')} className="inline-flex text-fg-muted">
                  <CircleCheck aria-hidden className="size-4" />
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('docpabean.tooltipVerified')}</TooltipContent>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      // Second column: direction and mode are what a row is scanned for.
      accessor: 'shipmentType',
      title: t('docpabean.colType'),
      textAlign: 'center',
      render: (item) => {
        const mot = resolveMot(item.motType);
        return (
          <span className="inline-flex items-center justify-center gap-1.5">
            <ShipmentTypeBadge isExport={isExportAju(item.nomorAju)} short />
            {/* No chip at all when the filing never recorded a mode: better a gap than a confident wrong icon. */}
            {mot && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <MotBadge mot={mot} />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{mot === 'SEA' ? t('docpabean.motSea') : t('docpabean.motAir')}</TooltipContent>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      accessor: 'namaPerusahaan',
      title: t('docpabean.colShipper'),
      cellsClassName: 'whitespace-normal',
      render: (item) => (
        <div className="min-w-56">
          <CompanyNameCell
            name={item.senderName || item.namaPerusahaan}
            logoUrl={item.shipperLogoUrl}
            logoOnDark={item.shipperLogoOnDark}
            website={item.shipperWebsite}
            country={item.shipperConsigneeCountry}
          />
        </div>
      ),
    },
    {
      accessor: 'buyerName',
      title: t('docpabean.colBuyer'),
      cellsClassName: 'whitespace-normal',
      render: (item) => (
        <div className="min-w-40">
          <CompanyNameCell
            name={item.buyerName}
            logoUrl={item.buyerLogoUrl}
            logoOnDark={item.buyerLogoOnDark}
            website={item.buyerWebsite}
          />
        </div>
      ),
    },
    {
      accessor: 'tanggalDaftar',
      title: t('docpabean.colDate'),
      textAlign: 'center',
      render: (item) => {
        const time = getTimeFromCreatedAt(item.createdAt);
        return (
          <span className="grid">
            <DataTableMono className="text-fg text-xs">{item.tanggalDaftar || '-'}</DataTableMono>
            {time && <DataTableMono className="text-[10px] text-fg-muted">{time}</DataTableMono>}
          </span>
        );
      },
    },
    {
      accessor: 'respon',
      title: t('docpabean.colRespon'),
      textAlign: 'center',
      render: (item) => <StatusBadge status={item.namaProses} />,
    },
    {
      accessor: 'status',
      title: t('docpabean.colStatus'),
      textAlign: 'center',
      render: (item) => {
        const firstStatus = item.statuses?.[0];
        // Deleted in Jarvis only (the filing at CEISA is untouched). It stacks above the CEISA statuses, which a
        // deleted document can still carry.
        const deletedBadge = item.isDeleted ? <Badge variant="err">{t('docpabean.deletedBadge')}</Badge> : null;
        if (!firstStatus) {
          return <div className="flex justify-center gap-1">{deletedBadge ?? dash}</div>;
        }
        return (
          <div className="grid justify-items-center gap-1">
            {deletedBadge}
            <StatusCellWithInfo statuses={item.statuses || []} />
          </div>
        );
      },
    },
    {
      accessor: 'jenisKategori',
      title: t('docpabean.colJenisKategori'),
      textAlign: 'center',
      render: (item) => {
        const jenis = (item.jenisEkspor || item.jenisImpor || '').toString().toUpperCase();
        const kategori = (item.kategoriEkspor || item.kategoriImpor || '').toString().toUpperCase();
        if (!jenis && !kategori) return dash;
        return (
          <div className="grid justify-items-center gap-1">
            {jenis && <Badge variant="neutral">{jenis}</Badge>}
            {kategori && <Badge variant="neutral">{kategori}</Badge>}
          </div>
        );
      },
    },
    {
      accessor: 'drafter',
      title: t('docpabean.colDrafter'),
      textAlign: 'center',
      render: (item) => <span className="text-fg text-xs">{item.drafterName || '-'}</span>,
    },
    {
      accessor: 'totalItem',
      title: t('docpabean.colTotalItem'),
      textAlign: 'center',
      render: (item) => <span className="text-fg text-xs tabular-nums">{item.totalItem || '-'}</span>,
    },
    {
      accessor: 'approvalStatus',
      title: t('docpabean.colApproval'),
      textAlign: 'center',
      render: (item) => {
        if (!item.approvalStatus) return dash;
        const label = t(APPROVAL_LABEL_KEYS[item.approvalStatus] || 'docpabean.approved');
        const relative = formatRelativeTime(item.approvalDate, t);
        return (
          <div className="grid justify-items-center gap-0.5">
            <Badge variant="neutral">{label ?? item.approvalStatus}</Badge>
            {relative && <span className="text-[10px] text-fg-muted">{relative}</span>}
          </div>
        );
      },
    },
    {
      accessor: 'actions',
      title: t('docpabean.colActions'),
      textAlign: 'center',
      render: (item) => {
        const isViewOnly = isViewOnlyFor(item);
        return (
          <DataTableRowActions>
            <StatusHistoryIconButton
              onOpen={() => {
                setHistory({
                  ajuNo: item.nomorAju,
                  totalItem: item.totalItem,
                  shipperCountry: item.shipperConsigneeCountry,
                });
                setHistoryOpen(true);
              }}
            />
            <DataTableRowAction
              label={isViewOnly ? t('docpabean.tooltipView') : t('docpabean.tooltipEdit')}
              tone="info"
              data-action={isViewOnly ? 'view' : 'edit'}
              onClick={() => handleEdit(item)}
            >
              {isViewOnly ? <Eye /> : <Pencil />}
            </DataTableRowAction>
            {/* Read-only, so view-only users get it too. */}
            <DataTableRowAction
              label={t(item.nomorAju ? 'docpabean.tooltipPreviewPdf' : 'docpabean.tooltipPreviewPdfNoAju')}
              tone="ai"
              data-action="pdf"
              disabled={!item.nomorAju}
              onClick={() => openPdfPreview(item)}
            >
              <FileText />
            </DataTableRowAction>
            {!isViewOnly && (
              <DataTableRowAction
                label={t('docpabean.tooltipCopy')}
                tone="ok"
                data-action="copy"
                aria-busy={copyLoading || undefined}
                disabled={copyLoading}
                onClick={() => handleCopy(item)}
              >
                {copyLoading ? SPIN : <Copy />}
              </DataTableRowAction>
            )}
            <DataTableRowAction
              label={t('docpabean.tooltipUpload')}
              tone="info"
              data-action="upload"
              onClick={() => openUpload(item)}
            >
              <Upload />
            </DataTableRowAction>
            <DataTableRowAction
              label={t('docpabean.tooltipSyncRespon')}
              tone="warn"
              data-action="sync"
              aria-busy={syncResponLoadingAju === item.nomorAju || undefined}
              disabled={syncResponLoadingAju === item.nomorAju}
              onClick={() => {
                void syncRespon(item.nomorAju);
              }}
            >
              {syncResponLoadingAju === item.nomorAju ? SPIN : <RefreshCw />}
            </DataTableRowAction>
            {!isViewOnly && <ConfirmRowAction item={item} mode={item.isDeleted ? 'restore' : 'delete'} />}
          </DataTableRowActions>
        );
      },
    },
  ];

  // Sync local pageSize with prop when it changes externally
  // as the old table, only on the prop's change.
  useEffect(() => {
    if (numberPageSize !== pageSize) {
      setPageSize(numberPageSize);
    }
  }, [numberPageSize]);

  // as the old table, only when the size changes.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Only reset to page 1 when pageSize actually changes (not on initial render)
    onPageChange?.(1);
    setNumberPageSize(pageSize);
  }, [pageSize]);

  // as the old table, page changes re-take the rows too.
  useEffect(() => {
    setRecords(data);
  }, [data, numberPage]);

  const totalRecords = totalPage * numberPageSize;

  return (
    <>
      <div className="grid min-w-0 grid-cols-2 gap-2 md:grid-cols-5">
        <FilterSelect
          placeholder={t('docpabean.filterShipmentType')}
          value={filters.shipmentType}
          onChange={setFilter('shipmentType')}
          options={[
            { value: 'export', label: t('docpabean.export') },
            { value: 'import', label: t('docpabean.import') },
          ]}
        />
        <Input
          placeholder={t('docpabean.filterNamaPerusahaan')}
          value={filters.namaPerusahaan}
          onChange={(e) => setFilter('namaPerusahaan')(e.target.value)}
        />
        <Input
          placeholder={t('docpabean.filterDrafter')}
          value={filters.drafterName}
          onChange={(e) => setFilter('drafterName')(e.target.value)}
        />
        <FilterSelect
          placeholder={t('docpabean.filterMotType')}
          value={filters.motType}
          onChange={setFilter('motType')}
          options={[
            { value: 'SEA', label: 'Sea' },
            { value: 'AIR', label: 'Air' },
          ]}
        />
        <Input
          placeholder={t('docpabean.filterCountry')}
          value={filters.shipperConsigneeCountry}
          onChange={(e) => setFilter('shipperConsigneeCountry')(e.target.value)}
        />
      </div>

      <div className="relative min-w-0" aria-busy={copyLoading || undefined}>
        {/* A copy POST blocks the table, as the old loading overlay did. */}
        {copyLoading && (
          <div className="absolute inset-0 z-20 grid place-items-center rounded-card bg-canvas/55 backdrop-blur-[1px]">
            <Loader2 aria-hidden className="size-6 animate-spin text-label motion-reduce:animate-none" />
          </div>
        )}
        {isMobile ? (
          <DocPabeanMobileCards
            records={filteredRecords}
            loading={loading}
            page={numberPage}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={(p) => onPageChange?.(p)}
            onView={handleMobileView}
            onCopy={handleCopy}
            onUpload={openUpload}
            onPreviewPdf={openPdfPreview}
            onSyncRespon={(item) => {
              void syncRespon(item.nomorAju);
            }}
            copyLoading={copyLoading}
            syncResponLoadingAju={syncResponLoadingAju}
          />
        ) : (
          <DataTable
            aria-label={t('docpabean.colNomorAju')}
            columns={columns}
            data={filteredRecords}
            loading={loading}
            totalRecords={totalRecords}
            currentPage={numberPage}
            pageSize={pageSize}
            onPageChange={(p) => onPageChange?.(p)}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setNumberPageSize(value);
            }}
            getRowKey={(r) => r.batchUUID}
            pageSizeOptions={[10, 20, 30, 40, 50]}
          />
        )}
      </div>

      <UnggahBerkasModal
        opened={unggahBerkasOpen}
        onClose={() => setUnggahBerkasOpen(false)}
        batchUUID={unggahBerkasBatchUUID}
      />
      {/* Declaration PDF (PIB/PEB): final NOPEN once registered, else the CEISA draft */}
      <DeclarationPreviewModal opened={pdfPreviewOpen} onClose={() => setPdfPreviewOpen(false)} ajuNo={pdfPreviewAju} />
      {/* Status history: one modal at table level (DataTable remounts cells on every render). */}
      <StatusHistoryModal
        opened={historyOpen}
        onClose={() => setHistoryOpen(false)}
        ajuNo={history?.ajuNo ?? ''}
        totalItem={history?.totalItem}
        shipperCountry={history?.shipperCountry}
      />
    </>
  );
}
